import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const ResultCard = ({ result }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  // تاریخ
  const formattedDate = result.createdAt
    ? new Date(result.createdAt).toLocaleDateString(isRtl ? "fa-IR" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : t("result_card.unknown");

  // زمان صرف‌شده (دقیقه)
  const timeSpentMin =
    result.startedAt && result.finishedAt
      ? Math.max(
          0,
          Math.round(
            (new Date(result.finishedAt) - new Date(result.startedAt)) / 60000
          )
        )
      : null;

  const timeSpentLabel =
    timeSpentMin != null
      ? `${timeSpentMin} ${t("result_card.minutes")}`
      : t("result_card.unknown");

  const percentage =
    typeof result.percentage === "number" ? result.percentage : 0;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-4 border border-gray-100 flex flex-col justify-between">
      {/* 🧾 عنوان و تاریخ */}
      <div className="mb-3">
        <h3 className="text-sm font-bold text-gray-800 line-clamp-1">
          {result.examTitle}
        </h3>
        <p className="text-xs text-gray-500 mt-1 flex items-center">
          <svg
            className="w-3.5 h-3.5 text-gray-400 me-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          {formattedDate}
        </p>
      </div>

      {/* 📊 نمره‌ها */}
      <div className="grid grid-cols-3 gap-3 text-xs mb-4">
        <div className="flex flex-col">
          <span className="text-gray-500">{t("result_card.score")}:</span>
          <span className="text-base font-bold text-blue-600">
            {result.score ?? 0}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-gray-500">{t("result_card.percentage")}:</span>
          <span className="text-base font-bold text-emerald-600">
            {percentage}%
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-gray-500">{t("result_card.time_spent")}:</span>
          <span className="text-sm font-semibold text-gray-700 flex items-center">
            <svg
              className={`w-4 h-4 text-gray-400 ${
                isRtl ? "ml-1" : "mr-1"
              } inline-block`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {timeSpentLabel}
          </span>
        </div>
      </div>

      {/* 🔗 دکمه جزئیات */}
      <Link
        to={`/results/${result._id}`}
        className="mt-auto text-center text-sm text-white py-2 font-semibold bg-indigo-600 hover:text-indigo-800 transition"
      >
        {t("result_card.view_report")}
      </Link>
    </div>
  );
};

ResultCard.propTypes = {
  result: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    examTitle: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
    startedAt: PropTypes.string,
    finishedAt: PropTypes.string,
    score: PropTypes.number,
    percentage: PropTypes.number,
  }).isRequired,
};

export default ResultCard;
